import { type Component, createSignal, Match, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTalentPool } from "@/domains/company/talent-pool/context/talent-pool-provider";
import { cn } from "@/libs/cn";
import { PremiumLockBadge } from "./premium-lock-badge";
import { SavePopover } from "./save-popover";

export type HeartButtonProps = {
  freelancerProfileId: string;
  /**
   * `search_id` della pagina di risultati della card che monta questo cuore.
   * Passato SOLO dalle card della ricerca attiva: i percorsi pool non lo
   * passano e il salvataggio parte senza il campo.
   */
  searchId?: string;
};

// Shared trigger frame ("Talent Pool — Piano Free" handoff): a 40px rounded
// square with a hairline border, the heart glyph centered. It is `relative` so
// the locked lock badge can anchor to its bottom-right corner.
const TRIGGER_BASE =
  "relative inline-flex h-10 w-10 items-center justify-center rounded-[10px] border transition-colors";

// Neutral / actionable heart (inert + unsaved): white tile, muted glyph that
// tints to the brand primary on hover.
const TRIGGER_DEFAULT =
  "border-border bg-white text-gray-500 hover:bg-gray-50 hover:text-primary";

// Locked (free plan): a faint tile whose glyph carries the premium lock badge.
const TRIGGER_LOCKED = "border-border bg-gray-50 text-gray-400";

// Saved presentation: a filled heart tinted with the brand primary so it reads
// as "in the Talent Pool", matching the footer `SavedPill`.
const TRIGGER_SAVED =
  "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15";

/**
 * `HeartButton` — the save affordance mounted in a fully-clickable freelancer
 * card. It derives its entire state from `useTalentPool()`, gating on
 * `capabilities.status`:
 *
 * - **inert** (`status !== "success"`, i.e. capabilities pending OR failed — D16):
 *   a neutral empty heart, no tooltip, no lock badge, no save/upsell. Never a
 *   premature save, never a false upsell.
 * - **locked** (`status === "success"` && `enabled === false`): empty heart +
 *   lock badge, tooltip "Sblocca il Talent Pool"; click opens the upsell (no POST).
 * - **unsaved** (`status === "success"` && `enabled === true` && no `entry`):
 *   empty heart, tooltip "Salva nel Talent Pool"; click saves via the provider,
 *   then opens the popover when the save RESOLVES (201 or 409-sync).
 * - **saved-own** (`entry` && `saved_by_me: true`): filled heart, tooltip
 *   "Salvato — modifica"; click re-opens the popover pre-filled (edit mode).
 * - **colleague-shared** (`entry` && `saved_by_me: false`): filled heart, no
 *   edit/remove affordance; click opens the popover, which renders read-only
 *   automatically (D6). The "Condiviso dal team" label lives on the footer pill.
 *
 * It owns the card-side `SavePopover` mount, anchored to the heart (desktop
 * popover / mobile drawer) and controlled by a local `popoverOpen` signal.
 */
export const HeartButton: Component<HeartButtonProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();

  const [popoverOpen, setPopoverOpen] = createSignal(false);
  // Stable anchor for the desktop popover — the wrapper is always mounted, so
  // it survives Switch branch swaps (unsaved → saved-own after a save).
  let heartEl!: HTMLSpanElement;

  const status = () => talentPool.capabilities.status;
  const entry = () => talentPool.entry(props.freelancerProfileId);
  const isLocked = () =>
    status() === "success" && talentPool.capabilities.enabled === false;
  const isUnsaved = () =>
    status() === "success" &&
    talentPool.capabilities.enabled === true &&
    entry() === undefined;
  // Saved presentation is suppressed while capabilities are inert (D16): the
  // neutral fallback wins until the query settles.
  const savedEntry = () => (status() === "success" ? entry() : undefined);
  const isSavedOwn = () => savedEntry()?.saved_by_me === true;
  const isColleague = () => savedEntry()?.saved_by_me === false;

  // Event isolation (critical): the heart lives inside a card whose whole body
  // opens the profile. Every heart click is stopped from bubbling so it never
  // triggers `onViewProfile`, and the card click never routes here. The action
  // is chosen from the resolved gating state; inert clicks are no-ops.
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (isLocked()) {
      talentPool.upsell.open("card");
      return;
    }
    if (isUnsaved()) {
      // The provider owns error handling (revert + toast / upsell routing); we
      // ignore the rejection here. The popover opens on RESOLVE (201/409-sync).
      void talentPool
        .save(props.freelancerProfileId, "card", props.searchId)
        .then(() => setPopoverOpen(true))
        .catch(() => {});
      return;
    }
    // saved-own (edit) or colleague-shared (read-only, D6): open the popover.
    // Inert entries fall through as a no-op.
    if (isSavedOwn() || isColleague()) {
      setPopoverOpen(true);
    }
  };

  return (
    <>
      <span ref={heartEl} class="inline-flex">
        <Switch
          fallback={
            // Inert (capabilities pending/failed — D16): a neutral empty heart
            // with no tooltip and no lock badge. The handler only guarantees
            // the card never reacts.
            <button
              type="button"
              class={cn(TRIGGER_BASE, TRIGGER_DEFAULT)}
              onClick={handleClick}
            >
              <i class="ph ph-heart text-xl" aria-hidden="true" />
            </button>
          }
        >
          <Match when={isLocked()}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                class={cn(TRIGGER_BASE, TRIGGER_LOCKED)}
                onClick={handleClick}
                aria-label={t("company.talent_pool.heart.tooltip_locked")}
              >
                <i class="ph ph-heart text-xl" aria-hidden="true" />
                {/* Premium lock badge anchored to the heart's bottom-right
                    corner — same mark carried by the locked Talent Pool tab. */}
                <PremiumLockBadge class="absolute -right-1.5 -bottom-1.5" />
              </TooltipTrigger>
              <TooltipContent>
                {t("company.talent_pool.heart.tooltip_locked")}
              </TooltipContent>
            </Tooltip>
          </Match>
          <Match when={isUnsaved()}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                class={cn(TRIGGER_BASE, TRIGGER_DEFAULT)}
                onClick={handleClick}
                aria-label={t("company.talent_pool.heart.tooltip_unsaved")}
              >
                <i class="ph ph-heart text-xl" aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>
                {t("company.talent_pool.heart.tooltip_unsaved")}
              </TooltipContent>
            </Tooltip>
          </Match>
          <Match when={isSavedOwn()}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                class={cn(TRIGGER_BASE, TRIGGER_SAVED)}
                onClick={handleClick}
                aria-label={t("company.talent_pool.heart.tooltip_saved")}
              >
                <i class="ph-fill ph-heart text-xl" aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>
                {t("company.talent_pool.heart.tooltip_saved")}
              </TooltipContent>
            </Tooltip>
          </Match>
          <Match when={isColleague()}>
            {/* Read-only saved look. No edit/remove affordance on the heart
                itself (D6); the click opens the read-only popover to view the
                colleague's note. */}
            <button
              type="button"
              class={cn(TRIGGER_BASE, TRIGGER_SAVED)}
              onClick={handleClick}
              aria-label={t("company.talent_pool.pill.colleague")}
            >
              <i class="ph-fill ph-heart text-xl" aria-hidden="true" />
            </button>
          </Match>
        </Switch>
      </span>

      <SavePopover
        open={popoverOpen()}
        onOpenChange={setPopoverOpen}
        freelancerProfileId={props.freelancerProfileId}
        anchor={() => heartEl}
      />
    </>
  );
};
